import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { TagInput } from '@/components/TagInput';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { VariableTextarea } from '@/components/VariableTextarea';
import { errorMessage } from '@/lib/api';
import type { JoinFlowSettings, KeywordDto, KeywordInput, KeywordKind, LanguageInfo } from '@/lib/types';
import { cn } from '@/lib/utils';

const RESERVED = ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'START', 'YES', 'UNSTOP', 'HELP', 'INFO'];
const KEYWORD_RE = /^[A-Z0-9][A-Z0-9_-]{0,23}$/;

export interface KeywordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Editing an existing keyword; omit to create. */
  keyword?: KeywordDto | null;
  /** Pre-select the kind for a new keyword. */
  defaultKind?: KeywordKind;
  /** Languages the org has enabled (translations are offered for all but the default). */
  languages: string[];
  defaultLanguage: string;
  languageCatalog: LanguageInfo[];
  joinDefaults: Required<JoinFlowSettings>;
  allowJoin: boolean;
  jobName: string;
  scopeLabel: string;
  onSubmit: (body: KeywordInput, id: string | null) => Promise<unknown>;
  busy?: boolean;
}

export function KeywordDialog({ open, onOpenChange, keyword, defaultKind = 'info', languages, defaultLanguage, languageCatalog, joinDefaults, allowJoin, jobName, scopeLabel, onSubmit, busy }: KeywordDialogProps) {
  const editing = !!keyword;
  const [kind, setKind] = useState<KeywordKind>(keyword?.kind ?? defaultKind);
  const [word, setWord] = useState(keyword?.keyword ?? (defaultKind === 'join' ? 'JOIN' : ''));
  const [replyBody, setReplyBody] = useState(keyword?.replyBody ?? '');
  const [translations, setTranslations] = useState<Record<string, string>>(keyword?.replyTranslations ?? {});
  const [enabled, setEnabled] = useState(keyword?.enabled ?? true);
  const [settings, setSettings] = useState<JoinFlowSettings>(keyword?.settings ?? {});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind(keyword?.kind ?? defaultKind);
    setWord(keyword?.keyword ?? (defaultKind === 'join' ? 'JOIN' : ''));
    setReplyBody(keyword?.replyBody ?? '');
    setTranslations(keyword?.replyTranslations ?? {});
    setEnabled(keyword?.enabled ?? true);
    setSettings(keyword?.settings ?? {});
    setError(null);
  }, [open, keyword, defaultKind]);

  const normalized = word.trim().toUpperCase();
  const wordError = !normalized ? 'Enter a keyword' : RESERVED.includes(normalized) ? `${normalized} is a carrier keyword and cannot be redefined` : !KEYWORD_RE.test(normalized) ? 'One word: letters, numbers, - or _ (max 24)' : null;
  const otherLanguages = useMemo(() => languages.filter((l) => l !== defaultLanguage), [languages, defaultLanguage]);
  const langName = (code: string) => languageCatalog.find((l) => l.code === code)?.name ?? code;
  const eff = { ...joinDefaults, ...stripUndefined(settings) };

  const submit = async () => {
    setError(null);
    if (wordError) {
      setError(wordError);
      return;
    }
    if (kind === 'info' && !replyBody.trim()) {
      setError('Enter the reply text');
      return;
    }
    try {
      await onSubmit(
        {
          keyword: normalized,
          kind,
          replyBody: replyBody.trim(),
          replyTranslations: Object.fromEntries(Object.entries(translations).filter(([, v]) => v.trim())),
          settings: kind === 'join' ? stripUndefined(settings) : {},
          enabled,
        },
        keyword?.id ?? null,
      );
      toast.success(editing ? 'Keyword updated' : 'Keyword added');
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${keyword?.keyword}` : kind === 'join' ? 'Set up self-service sign-up' : 'Add a keyword'}</DialogTitle>
          <DialogDescription>
            {kind === 'join'
              ? `Workers text this word to the job number (or scan a printed QR sign) to join the list. The reply explains what they are agreeing to and walks them through a few questions.`
              : `Anyone who texts this word to the job number gets the reply below, instantly. ${scopeLabel}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          <FormError message={error} />

          {!editing && allowJoin && (
            <RadioGroup value={kind} onValueChange={(v) => setKind(v as KeywordKind)} className="grid gap-2 sm:grid-cols-2">
              <label className={cn('flex items-start gap-3 border p-3', kind === 'info' && 'border-primary')}>
                <RadioGroupItem value="info" className="mt-0.5" />
                <span className="grid gap-0.5">
                  <span className="text-sm font-medium">Info reply</span>
                  <span className="text-muted-foreground text-xs">GATE, PARKING, HOURS, SAFETY… a fixed answer.</span>
                </span>
              </label>
              <label className={cn('flex items-start gap-3 border p-3', kind === 'join' && 'border-primary')}>
                <RadioGroupItem value="join" className="mt-0.5" />
                <span className="grid gap-0.5">
                  <span className="text-sm font-medium">Sign-up (JOIN)</span>
                  <span className="text-muted-foreground text-xs">Creates a contact with a consent record.</span>
                </span>
              </label>
            </RadioGroup>
          )}

          <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
            <Field label="Keyword" htmlFor="kw" required error={word && wordError ? wordError : undefined} description="Case doesn't matter when people text it.">
              <Input id="kw" value={word} onChange={(e) => setWord(e.target.value.toUpperCase())} placeholder={kind === 'join' ? 'JOIN' : 'GATE'} className="font-mono uppercase" maxLength={24} autoFocus={!editing} />
            </Field>
            <div className="flex items-center gap-3 self-end pb-1">
              <Switch id="kw-enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="kw-enabled" className="font-normal">
                {enabled ? 'Enabled — replies are sent' : 'Disabled — no reply is sent'}
              </Label>
            </div>
          </div>

          {kind === 'info' ? (
            <>
              <Field label={`Reply (${langName(defaultLanguage)})`} htmlFor="kw-reply" required description={<>Plain text, ideally one segment (160 characters). <code className="text-[11px]">{'{{job.name}}'}</code> is replaced with the job name. The sender label is added like on every text.</>}>
                <VariableTextarea id="kw-reply" rows={4} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} maxLength={1000} placeholder="Gate B is closed this week. Use Gate A on 5th Street; badge in at the trailer." />
                <span className={cn('text-right text-xs tabular-nums', replyBody.length > 160 ? 'text-warning' : 'text-muted-foreground')}>{replyBody.length} / 160 for a single segment</span>
              </Field>
              {otherLanguages.length > 0 && (
                <div className="grid gap-3 border p-3">
                  <div>
                    <h4 className="text-sm font-medium">Other languages</h4>
                    <p className="text-muted-foreground text-xs">Leave blank to machine-translate automatically for contacts whose language is set. Fill in to control the wording exactly.</p>
                  </div>
                  {otherLanguages.map((code) => (
                    <Field key={code} label={langName(code)} htmlFor={`kw-tr-${code}`}>
                      <Textarea id={`kw-tr-${code}`} rows={2} value={translations[code] ?? ''} onChange={(e) => setTranslations((t) => ({ ...t, [code]: e.target.value }))} maxLength={1000} placeholder={`Optional ${langName(code)} version`} />
                    </Field>
                  ))}
                </div>
              )}
            </>
          ) : (
            <JoinSettingsForm settings={settings} effective={eff} onChange={setSettings} jobName={jobName} languages={languages} langName={langName} />
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : kind === 'join' ? 'Turn on sign-up' : 'Add keyword'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

export function JoinSettingsForm({ settings, effective, onChange, jobName, languages, langName, compact }: { settings: JoinFlowSettings; effective: Required<JoinFlowSettings>; onChange: (s: JoinFlowSettings) => void; jobName: string; languages: string[]; langName: (c: string) => string; compact?: boolean }) {
  const set = <K extends keyof JoinFlowSettings>(k: K, v: JoinFlowSettings[K]) => onChange({ ...settings, [k]: v });
  const Toggle = ({ k, label, hint }: { k: 'askLanguage' | 'askName' | 'askTrade' | 'askCompany'; label: string; hint: string }) => (
    <div className="flex items-start justify-between gap-4 border p-3">
      <div>
        <Label htmlFor={`join-${k}`} className="text-sm font-medium">
          {label}
        </Label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
      <Switch id={`join-${k}`} checked={effective[k]} onCheckedChange={(v) => set(k, v)} />
    </div>
  );
  const steps = [effective.askLanguage && languages.length > 1 ? 'language' : null, effective.askName ? 'name' : null, effective.askTrade ? 'trade' : null, effective.askCompany ? 'company' : null].filter(Boolean) as string[];
  return (
    <div className={cn('grid gap-4', !compact && 'lg:grid-cols-[1fr_260px]')}>
      <div className="grid gap-3">
        <Field label="Consent disclosure" htmlFor="join-consent" description="Sent with the first prompt and stored on every consent record. Say who is texting, what about, and how to stop.">
          <Textarea id="join-consent" rows={3} value={effective.consentText} onChange={(e) => set('consentText', e.target.value)} maxLength={600} />
        </Field>
        <div className="grid gap-2">
          <Label>Questions to ask</Label>
          <Toggle k="askLanguage" label="Preferred language" hint={languages.length > 1 ? `Offers ${languages.map(langName).join(', ')}; replies and future texts use it.` : 'Enable more languages in Organization → Settings to ask this.'} />
          <Toggle k="askName" label="Full name" hint="Split into first and last name." />
          <Toggle k="askTrade" label="Trade" hint="A numbered list; the answer becomes the title and a tag." />
          <Toggle k="askCompany" label="Company" hint="Free text; workers can reply SKIP." />
        </div>
        {effective.askTrade && (
          <Field label="Trade options" htmlFor="join-trades" description="Up to 12; shown as a numbered list.">
            <TagInput id="join-trades" value={effective.tradeOptions} onChange={(v) => set('tradeOptions', v.slice(0, 12))} placeholder="Add a trade and press Enter" />
          </Field>
        )}
        <Field label="Tags for everyone who joins" htmlFor="join-tags">
          <TagInput id="join-tags" value={effective.tags} onChange={(v) => set('tags', v)} placeholder="e.g. self-service" />
        </Field>
        <Field label="Welcome text" htmlFor="join-welcome" description={<>Sent when the sign-up is complete. <code className="text-[11px]">{'{{job.name}}'}</code> is replaced.</>}>
          <Textarea id="join-welcome" rows={2} value={effective.welcomeBody} onChange={(e) => set('welcomeBody', e.target.value)} maxLength={600} />
        </Field>
      </div>
      <aside className="grid content-start gap-2">
        <Label>How it reads</Label>
        <div className="bg-muted/40 grid gap-2 border p-3 text-xs">
          <Bubble who="worker">JOIN</Bubble>
          <Bubble who="us">
            {effective.consentText.replace(/\{\{\s*job\.name\s*\}\}/g, jobName)}
            {steps[0] === 'language' && (
              <>
                {'\n\n'}Reply with the number for your language:
                {languages.map((l, i) => `\n${i + 1} ${langName(l)}`)}
              </>
            )}
            {steps[0] === 'name' && '\n\nWhat is your full name?'}
            {steps[0] === 'trade' && `\n\nWhat is your trade? Reply with a number:${effective.tradeOptions.map((t, i) => `\n${i + 1} ${t}`).join('')}`}
            {steps[0] === 'company' && '\n\nWhich company do you work for?'}
          </Bubble>
          {steps.length > 1 && <p className="text-muted-foreground text-center text-[11px]">… {steps.length - 1} more question{steps.length - 1 === 1 ? '' : 's'} …</p>}
          <Bubble who="us">{effective.welcomeBody.replace(/\{\{\s*job\.name\s*\}\}/g, jobName)}</Bubble>
        </div>
      </aside>
    </div>
  );
}

export function Bubble({ who, children }: { who: 'us' | 'worker'; children: React.ReactNode }) {
  return (
    <div className={cn('max-w-[92%] px-3 py-2 whitespace-pre-wrap', who === 'us' ? 'bg-secondary text-secondary-foreground self-start' : 'bg-primary text-primary-foreground self-end justify-self-end')}>{children}</div>
  );
}
